import ManageAccountsRoundedIcon from '@mui/icons-material/ManageAccountsRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import VerifiedUserRoundedIcon from '@mui/icons-material/VerifiedUserRounded';
import type { SvgIconProps } from '@mui/material/SvgIcon';
import { TFunction } from 'i18next';
import type { ComponentType } from 'react';
import { EnumValues } from '../types/enums';

export const NEOBET_PLAYER_ACCOUNT_MENU_ITEM = {
  ACCOUNT: 'account',
  MY_BETS: 'my-bets',
  HISTORY: 'history',
  MESSAGES: 'messages',
  BONUS: 'bonus',
  LOGOUT: 'logout',
} as const;

export type NeobetPlayerAccountMenuItemKey = EnumValues<typeof NEOBET_PLAYER_ACCOUNT_MENU_ITEM>;

export type NeobetPlayerAccountMenuItem = {
  key: NeobetPlayerAccountMenuItemKey;
  label: string;
  path?: string;
  mobileOnly?: boolean;
};

export const NEOBET_PLAYER_ACCOUNT_PATHS = {
  ACCOUNT: '/my-account',
  MY_BETS: '/my-bets',
  HISTORY: '/my-account/history',
  MESSAGES: '/my-account/messages/inbox',
  BONUS: '/my-account/bonus',
} as const;

export const NEOBET_ACCOUNT_TAB = {
  GENERAL: 'general',
  VERIFICATION: 'verification',
  SECURITY: 'security',
  PREFERENCES: 'preferences',
} as const;

export type NeobetAccountTabKey = EnumValues<typeof NEOBET_ACCOUNT_TAB>;

export type NeobetAccountTab = {
  key: NeobetAccountTabKey;
  icon: ComponentType<SvgIconProps>;
  label: string;
  path: string;
  disabled?: boolean;
};

export const NEOBET_ACCOUNT_TAB_PATHS = {
  [NEOBET_ACCOUNT_TAB.GENERAL]: '/my-account',
  [NEOBET_ACCOUNT_TAB.VERIFICATION]: '/my-account/verification',
  [NEOBET_ACCOUNT_TAB.SECURITY]: '/my-account/security',
  [NEOBET_ACCOUNT_TAB.PREFERENCES]: '/my-account/preferences',
} as const;

const neobetPlayerAccountMenuBuilders: Record<
  NeobetPlayerAccountMenuItemKey,
  (t: TFunction) => NeobetPlayerAccountMenuItem
> = {
  [NEOBET_PLAYER_ACCOUNT_MENU_ITEM.ACCOUNT]: (t) => ({
    key: NEOBET_PLAYER_ACCOUNT_MENU_ITEM.ACCOUNT,
    label: t('account'),
    path: NEOBET_PLAYER_ACCOUNT_PATHS.ACCOUNT,
  }),
  [NEOBET_PLAYER_ACCOUNT_MENU_ITEM.MY_BETS]: (t) => ({
    key: NEOBET_PLAYER_ACCOUNT_MENU_ITEM.MY_BETS,
    label: t('myBets', 'Bets'),
    path: NEOBET_PLAYER_ACCOUNT_PATHS.MY_BETS,
  }),
  [NEOBET_PLAYER_ACCOUNT_MENU_ITEM.HISTORY]: (t) => ({
    key: NEOBET_PLAYER_ACCOUNT_MENU_ITEM.HISTORY,
    label: t('history'),
    path: NEOBET_PLAYER_ACCOUNT_PATHS.HISTORY,
  }),
  [NEOBET_PLAYER_ACCOUNT_MENU_ITEM.MESSAGES]: (t) => ({
    key: NEOBET_PLAYER_ACCOUNT_MENU_ITEM.MESSAGES,
    label: t('messages'),
    path: NEOBET_PLAYER_ACCOUNT_PATHS.MESSAGES,
  }),
  [NEOBET_PLAYER_ACCOUNT_MENU_ITEM.BONUS]: (t) => ({
    key: NEOBET_PLAYER_ACCOUNT_MENU_ITEM.BONUS,
    label: t('bonus'),
    path: NEOBET_PLAYER_ACCOUNT_PATHS.BONUS,
  }),
  [NEOBET_PLAYER_ACCOUNT_MENU_ITEM.LOGOUT]: (t) => ({
    key: NEOBET_PLAYER_ACCOUNT_MENU_ITEM.LOGOUT,
    label: t('logout'),
  }),
};

const NEOBET_PLAYER_ACCOUNT_MENU_ORDER: NeobetPlayerAccountMenuItemKey[] = [
  NEOBET_PLAYER_ACCOUNT_MENU_ITEM.ACCOUNT,
  NEOBET_PLAYER_ACCOUNT_MENU_ITEM.MY_BETS,
  NEOBET_PLAYER_ACCOUNT_MENU_ITEM.HISTORY,
  NEOBET_PLAYER_ACCOUNT_MENU_ITEM.MESSAGES,
  NEOBET_PLAYER_ACCOUNT_MENU_ITEM.BONUS,
  NEOBET_PLAYER_ACCOUNT_MENU_ITEM.LOGOUT,
];

export const getNeobetPlayerAccountMenuItems = (t: TFunction, isMobile = false): NeobetPlayerAccountMenuItem[] =>
  NEOBET_PLAYER_ACCOUNT_MENU_ORDER.map((key) => neobetPlayerAccountMenuBuilders[key](t)).filter(
    (item) => !item.mobileOnly || isMobile
  );

const NEOBET_ACCOUNT_TAB_ORDER: NeobetAccountTabKey[] = [
  NEOBET_ACCOUNT_TAB.GENERAL,
  NEOBET_ACCOUNT_TAB.VERIFICATION,
  NEOBET_ACCOUNT_TAB.SECURITY,
  NEOBET_ACCOUNT_TAB.PREFERENCES,
];

type GetNeobetAccountTabsOptions = {
  includeVerification?: boolean;
};

const neobetAccountTabBuilders: Record<NeobetAccountTabKey, (t: TFunction) => NeobetAccountTab> = {
  [NEOBET_ACCOUNT_TAB.GENERAL]: (t) => ({
    key: NEOBET_ACCOUNT_TAB.GENERAL,
    icon: ManageAccountsRoundedIcon,
    label: t('general', 'General'),
    path: NEOBET_ACCOUNT_TAB_PATHS[NEOBET_ACCOUNT_TAB.GENERAL],
  }),
  [NEOBET_ACCOUNT_TAB.VERIFICATION]: (t) => ({
    key: NEOBET_ACCOUNT_TAB.VERIFICATION,
    icon: VerifiedUserRoundedIcon,
    label: t('kyc.verification', 'Verification'),
    path: NEOBET_ACCOUNT_TAB_PATHS[NEOBET_ACCOUNT_TAB.VERIFICATION],
  }),
  [NEOBET_ACCOUNT_TAB.SECURITY]: (t) => ({
    key: NEOBET_ACCOUNT_TAB.SECURITY,
    icon: LockRoundedIcon,
    label: t('security', 'Security'),
    path: NEOBET_ACCOUNT_TAB_PATHS[NEOBET_ACCOUNT_TAB.SECURITY],
  }),
  [NEOBET_ACCOUNT_TAB.PREFERENCES]: (t) => ({
    key: NEOBET_ACCOUNT_TAB.PREFERENCES,
    icon: TuneRoundedIcon,
    label: t('preferences', 'Preferences'),
    path: NEOBET_ACCOUNT_TAB_PATHS[NEOBET_ACCOUNT_TAB.PREFERENCES],
  }),
};

export const getNeobetAccountTabs = (t: TFunction, options?: GetNeobetAccountTabsOptions): NeobetAccountTab[] =>
  NEOBET_ACCOUNT_TAB_ORDER.filter((key) => key !== NEOBET_ACCOUNT_TAB.VERIFICATION || options?.includeVerification).map(
    (key) => neobetAccountTabBuilders[key](t)
  );

export const getNeobetAccountTabFromPath = (
  pathname: string,
  options?: GetNeobetAccountTabsOptions
): NeobetAccountTabKey => {
  if (options?.includeVerification && pathname.startsWith(NEOBET_ACCOUNT_TAB_PATHS[NEOBET_ACCOUNT_TAB.VERIFICATION])) {
    return NEOBET_ACCOUNT_TAB.VERIFICATION;
  }

  if (pathname.startsWith(NEOBET_ACCOUNT_TAB_PATHS[NEOBET_ACCOUNT_TAB.SECURITY])) {
    return NEOBET_ACCOUNT_TAB.SECURITY;
  }

  if (pathname.startsWith(NEOBET_ACCOUNT_TAB_PATHS[NEOBET_ACCOUNT_TAB.PREFERENCES])) {
    return NEOBET_ACCOUNT_TAB.PREFERENCES;
  }

  return NEOBET_ACCOUNT_TAB.GENERAL;
};
